import React from "react"
import dayjs from "utils/dates"

// antd
import { Tag, Typography, Button, Space, Drawer, Divider, Flex } from "antd"
import {
  SmileFilled,
  MenuOutlined,
  UsbFilled,
  ShopFilled,
  CalendarFilled,
  LogoutOutlined,
} from "@ant-design/icons"

// store
import { useAuth } from "store/auth"
import { useTranslation } from "react-i18next"

// components
import MenuDrawerAccount from "./MenuDrawerAccount"
import MenuDrawerOrders from "./MenuDrawerOrders"
import MenuDrawerReservations from "./MenuDrawerReservations"
import MenuDrawerLogout from "./MenuDrawerLogout"
import LoginModal from "modals/Login"
import Logo from "./Logo"
import SignupModal from "modals/Signup"

const UserInfoMenu = () => {
  const { user, isAuthentificated, status } = useAuth()
  const { t } = useTranslation()

  const [drawerOpen, setDrawerOpen] = React.useState(false)

  const [showAccountDrawer, setShowAccountDrawer] = React.useState(false)
  const [showOrdersDrawer, setShowOrdersDrawer] = React.useState(false)
  const [showReservationsDrawer, setShowReservationsDrawer] =
    React.useState(false)
  const [showLogoutDrawer, setShowLogoutDrawer] = React.useState(false)

  const [showLoginModal, setShowLoginModal] = React.useState(false)
  const [showSignupModal, setShowSignupModal] = React.useState(false)

  return (
    <div>
      <LoginModal
        open={showLoginModal && !isAuthentificated}
        onCancel={() => setShowLoginModal(false)}
        onFinish={() => setShowLoginModal(false)}
      />

      <SignupModal
        open={showSignupModal}
        onCancel={() => setShowSignupModal(false)}
        onFinish={() => setShowSignupModal(false)}
      />

      <Drawer
        placement="right"
        title={t("menu_title")}
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
      >
        <MenuDrawerOrders
          open={showOrdersDrawer}
          onClose={() => setShowOrdersDrawer(false)}
        />

        <MenuDrawerReservations
          open={showReservationsDrawer}
          onClose={() => setShowReservationsDrawer(false)}
        />

        <MenuDrawerAccount
          open={showAccountDrawer}
          onClose={() => setShowAccountDrawer(false)}
        />

        <MenuDrawerLogout
          open={showLogoutDrawer}
          onClose={() => setShowLogoutDrawer(false)}
          onLogout={() => {
            setShowLogoutDrawer(false)
            setDrawerOpen(false)
          }}
        />

        <Typography.Title level={4}>Hi!</Typography.Title>

        {user && (
          <Tag
            bordered={false}
            color="purple"
            icon={<SmileFilled style={{ fontSize: 20 }} />}
          >
            <Space>
              {user.isAnonymous ? (
                <Typography.Text strong italic>
                  {t("guest")}
                </Typography.Text>
              ) : user?.data?.firstname && user?.data?.familyname ? (
                <Typography.Text strong italic>
                  {user.data.firstname} {user.data.familyname}
                </Typography.Text>
              ) : (
                <Typography.Text strong italic>
                  {user.email}
                </Typography.Text>
              )}
            </Space>
          </Tag>
        )}

        <Divider />

        {user?.isAnonymous && (
          <>
            <Typography.Paragraph>
              {t("guest_register_prompt")}
            </Typography.Paragraph>

            <Button
              block
              type="dashed"
              onClick={() => {
                setShowSignupModal(true)
              }}
            >
              {t("register")}
            </Button>

            <Divider />
          </>
        )}

        <Typography.Paragraph>{t("welcome_back_text")}</Typography.Paragraph>

        <Divider />

        <Space direction="vertical" style={{ width: "100%" }}>
          <Button
            block
            onClick={() => setShowAccountDrawer(true)}
            icon={<UsbFilled />}
          >
            {t("account_settings")}
          </Button>
          <Button
            block
            onClick={() => setShowOrdersDrawer(true)}
            icon={<ShopFilled />}
          >
            {t("orders_btn")}
          </Button>
          <Button
            block
            onClick={() => setShowReservationsDrawer(true)}
            icon={<CalendarFilled />}
          >
            {t("reservations_btn")}
          </Button>

          <Divider />

          <Button
            icon={<LogoutOutlined />}
            block
            danger
            onClick={() => setShowLogoutDrawer(true)}
          >
            {t("sign_out")}
          </Button>

          <Divider />
          <Space
            direction="vertical"
            size="small"
            align="center"
            style={{ width: "100%", gap: 2 }}
          >
            <Typography.Text
              type="secondary"
              style={{ fontStyle: "italic", fontSize: 12 }}
            >
              {t("restaurant_managed_by")}
            </Typography.Text>
            <Logo />
            <Typography.Paragraph align="center" style={{ lineHeight: 0.5 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Version: {process.env.version} (
                {dayjs(process.env.buildDate).format("MMM YYYY")})
              </Typography.Text>
            </Typography.Paragraph>
          </Space>
        </Space>
      </Drawer>

      <Button
        loading={status !== "ready"}
        size="small"
        color="purple"
        variant="filled"
        icon={<SmileFilled style={{ fontSize: 14 }} />}
        onClick={() => {
          if (!user) {
            setShowLoginModal(true)
            return
          }
          setDrawerOpen(true)
        }}
      >
        <Space>
          {!user ? (
            t("menu_title")
          ) : user.isAnonymous ? (
            <Typography.Text strong>{t("guest")}</Typography.Text>
          ) : user?.data?.firstname && user?.data?.familyname ? (
            <Typography.Text strong>
              {user.data.firstname} {user.data.familyname}
            </Typography.Text>
          ) : (
            <Typography.Text strong>{user.email}</Typography.Text>
          )}

          <MenuOutlined />
        </Space>
      </Button>
    </div>
  )
}

export default UserInfoMenu
